import { VIDEO_MEDIA_KINDS, resolveMediaPlayback } from './mediaSupport';
import type {
  DialogueCameraCue,
  DialogueLine,
  DialogueMediaCue,
  DialogueScript,
  MediaCueEnd,
  MediaCuePlay,
  MediaCueWhen,
  PlacedObject,
} from '../types';

export const DIALOGUE_CAMERA_TRANSITION_MS = 720;
export const MEDIA_CUE_DEFAULT_PREPLAY_MS = 1500;
export const MEDIA_CUE_NEARBY_RADIUS = 1;
export const MEDIA_CUE_CAMERA_FOV = 42;
/** Max distance for in-frustum visibility (matches typical room scale). */
export const MEDIA_CUE_MAX_VIEW_DISTANCE = 16;
export const MEDIA_CUE_DEFAULT_APPROACH_RADIUS = 3.2;

const videoKindSet = new Set<string>(VIDEO_MEDIA_KINDS);

export function isVideoDisplayObject(object: PlacedObject): boolean {
  return videoKindSet.has(object.kind);
}

export function mediaCuesOf(script: DialogueScript): DialogueMediaCue[] {
  return script.mediaCues ?? [];
}

export function resolveEffectiveCamera(
  lines: DialogueLine[],
  index: number,
): DialogueCameraCue | null {
  for (let i = Math.min(index, lines.length - 1); i >= 0; i -= 1) {
    if (lines[i]?.camera) return lines[i].camera!;
  }
  return null;
}

function distance3D(
  a: [number, number, number],
  b: [number, number, number],
) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.hypot(dx, dy, dz);
}

/** Screen-ish center for FOV tests (base position + half height). */
function displayViewPoint(object: PlacedObject): [number, number, number] {
  return [
    object.position[0],
    object.position[1] + object.dimensions.height * 0.45,
    object.position[2],
  ];
}

/** True when the display sits inside the cut camera cone (StudioScene fov ≈ 42). */
export function isDisplayInCameraView(
  camera: DialogueCameraCue,
  object: PlacedObject,
  fovDeg = MEDIA_CUE_CAMERA_FOV,
  maxDistance = MEDIA_CUE_MAX_VIEW_DISTANCE,
): boolean {
  const [px, py, pz] = camera.position;
  const [tx, ty, tz] = camera.target;
  const [ox, oy, oz] = displayViewPoint(object);

  const fdx = tx - px;
  const fdy = ty - py;
  const fdz = tz - pz;
  const flen = Math.hypot(fdx, fdy, fdz);
  if (flen < 0.001) return false;

  const fnx = fdx / flen;
  const fny = fdy / flen;
  const fnz = fdz / flen;

  const odx = ox - px;
  const ody = oy - py;
  const odz = oz - pz;
  const dist = Math.hypot(odx, ody, odz);
  if (dist < 0.05) return true;
  if (dist > maxDistance) return false;

  const onx = odx / dist;
  const ony = ody / dist;
  const onz = odz / dist;
  const dot = fnx * onx + fny * ony + fnz * onz;
  if (dot <= 0) return false;

  const halfFovRad = (fovDeg / 2) * (Math.PI / 180);
  // Widen slightly for horizontal FOV at ~16:9 aspect.
  const cosLimit = Math.cos(halfFovRad * 1.32);
  return dot >= cosLimit;
}

/** In cut view cone OR within `radius` meters of the camera. */
export function isDisplayRelevantToCut(
  camera: DialogueCameraCue,
  object: PlacedObject,
  radius = MEDIA_CUE_NEARBY_RADIUS,
): boolean {
  const viewPoint = displayViewPoint(object);
  if (distance3D(camera.position, viewPoint) <= radius) return true;
  return isDisplayInCameraView(camera, object);
}

export function isVideoPlaybackDisplay(object: PlacedObject): boolean {
  if (!isVideoDisplayObject(object)) return false;
  return resolveMediaPlayback(object.kind, object.mediaName, object.mediaUrl) === 'video';
}

/** Video-capable displays near a cut camera. Optionally require an attached video file. */
export function findNearbyVideoDisplays(
  objects: PlacedObject[],
  camera: DialogueCameraCue | null,
  options?: { requireSource?: boolean },
): PlacedObject[] {
  const nearby = findNearbyDisplays(objects, camera);
  if (options?.requireSource) {
    return nearby.filter(isVideoPlaybackDisplay);
  }
  return nearby;
}

export const MEDIA_CUE_WHEN_OPTIONS: Array<{ id: MediaCueWhen; label: string }> = [
  { id: 'onCut', label: '컷에 맞춰' },
  { id: 'onApproach', label: '다가올 때' },
  { id: 'prePlay', label: '미리 틀기' },
];

/** Displays in cut camera view or within `radius` (default 1 m). */
export function findNearbyDisplays(
  objects: PlacedObject[],
  camera: DialogueCameraCue | null,
  radius = MEDIA_CUE_NEARBY_RADIUS,
): PlacedObject[] {
  const displays = objects.filter(isVideoDisplayObject);
  if (!camera) return [];
  return displays
    .filter((object) => isDisplayRelevantToCut(camera, object, radius))
    .map((object) => ({
      object,
      distance: distance3D(camera.position, displayViewPoint(object)),
    }))
    .sort((a, b) => a.distance - b.distance)
    .map((entry) => entry.object);
}

export function createMediaCue(
  objectId: string,
  lineId: string,
  when: MediaCueWhen = 'onCut',
  play: MediaCuePlay = 'once',
  end: MediaCueEnd = 'hold',
): DialogueMediaCue {
  return {
    id: crypto.randomUUID(),
    objectId,
    lineId,
    when,
    play,
    end,
    prePlayMs: MEDIA_CUE_DEFAULT_PREPLAY_MS,
    approachRadius: MEDIA_CUE_DEFAULT_APPROACH_RADIUS,
  };
}

/** Upsert so each objectId keeps a single active cue. */
export function upsertMediaCue(
  cues: DialogueMediaCue[],
  next: DialogueMediaCue,
): { cues: DialogueMediaCue[]; replaced: boolean } {
  const without = cues.filter((cue) => cue.objectId !== next.objectId && cue.id !== next.id);
  const replaced = without.length < cues.length;
  return { cues: [...without, next], replaced };
}

export function cuesForLine(cues: DialogueMediaCue[], lineId: string): DialogueMediaCue[] {
  return cues.filter((cue) => cue.lineId === lineId);
}

export function pruneMediaCues(
  cues: DialogueMediaCue[],
  options: { objectIds?: Set<string>; lineIds?: Set<string> },
): DialogueMediaCue[] {
  return cues.filter((cue) => {
    if (options.objectIds && !options.objectIds.has(cue.objectId)) return false;
    if (options.lineIds && !options.lineIds.has(cue.lineId)) return false;
    return true;
  });
}

export function mediaCueWhenLabel(when: MediaCueWhen): string {
  if (when === 'onApproach') return '다가올 때';
  if (when === 'prePlay') return '미리 틀기';
  return '컷에 맞춰';
}

export function mediaCuePlayLabel(play: MediaCuePlay, end: MediaCueEnd): string {
  if (play === 'loop') return '반복';
  if (end === 'stop' || end === 'clear') return '한 번·정지';
  return '한 번·홀드';
}

export function cycleMediaCueWhen(when: MediaCueWhen): MediaCueWhen {
  if (when === 'onCut') return 'onApproach';
  if (when === 'onApproach') return 'prePlay';
  return 'onCut';
}

export function cycleMediaCuePlay(play: MediaCuePlay, end: MediaCueEnd): { play: MediaCuePlay; end: MediaCueEnd } {
  if (play === 'once' && end === 'hold') return { play: 'loop', end: 'hold' };
  if (play === 'loop') return { play: 'once', end: 'stop' };
  return { play: 'once', end: 'hold' };
}

export type DisplayMediaMode = 'free' | 'paused' | 'playing' | 'hold' | 'stop';

export interface DisplayMediaState {
  objectId: string;
  mode: DisplayMediaMode;
  loop: boolean;
  reset: boolean;
}

/**
 * Deterministic display state for seek / scrub:
 * - before cue line → paused at 0
 * - on / after cue line → playing or hold/stop based on play+end
 * - onApproach before transition completes → paused (caller may upgrade mid-lerp)
 */
export function resolveDisplayStatesAtLine(
  cues: DialogueMediaCue[],
  lines: DialogueLine[],
  activeIndex: number,
  options?: { approachSatisfied?: Set<string> },
): DisplayMediaState[] {
  const approachSatisfied = options?.approachSatisfied;
  return cues.map((cue) => {
    const cueIndex = lines.findIndex((line) => line.id === cue.lineId);
    if (cueIndex < 0 || activeIndex < 0) {
      return { objectId: cue.objectId, mode: 'paused' as const, loop: false, reset: true };
    }

    const past = activeIndex > cueIndex;
    const current = activeIndex === cueIndex;
    const loop = cue.play === 'loop';

    if (past) {
      if (cue.play === 'loop') {
        return { objectId: cue.objectId, mode: 'playing', loop: true, reset: false };
      }
      if (cue.end === 'hold') {
        return { objectId: cue.objectId, mode: 'hold', loop: false, reset: false };
      }
      return { objectId: cue.objectId, mode: 'stop', loop: false, reset: false };
    }

    if (!current) {
      // Previous line may still be in prePlay window — treat as paused until App fires.
      return { objectId: cue.objectId, mode: 'paused', loop: false, reset: true };
    }

    if (cue.when === 'onApproach' && !approachSatisfied?.has(cue.id)) {
      return { objectId: cue.objectId, mode: 'paused', loop: false, reset: true };
    }

    return {
      objectId: cue.objectId,
      mode: 'playing',
      loop,
      reset: true,
    };
  });
}

/** Absolute start offset (ms) of each line from timeline 0. */
export function lineStartOffsetsMs(lines: DialogueLine[]): number[] {
  const offsets: number[] = [];
  let acc = 0;
  for (const line of lines) {
    offsets.push(acc);
    acc += Math.max(800, line.durationMs);
  }
  return offsets;
}

/** When a cue should fire on the absolute timeline (ms from playback start). */
export function cueFireTimeMs(
  cue: DialogueMediaCue,
  lines: DialogueLine[],
  lineStarts: number[],
): number | null {
  const index = lines.findIndex((line) => line.id === cue.lineId);
  if (index < 0) return null;
  const lineStart = lineStarts[index] ?? 0;
  if (cue.when === 'prePlay') {
    const pre = cue.prePlayMs ?? MEDIA_CUE_DEFAULT_PREPLAY_MS;
    return Math.max(0, lineStart - pre);
  }
  if (cue.when === 'onApproach') {
    // Nominal fire at cut start; scene refines by camera distance during transition.
    return lineStart;
  }
  return lineStart;
}
